import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { toast } from 'react-toastify';

import DefaultLoader from '~/components/DefaultLoader';
import { HistoricoProduto } from '~/components/HistoricoProduto';
import { InputMoney } from '~/components/NovosInputs';
import { formatCurrencyAsText } from '~/utils/functions';

import { useAlteracaoPrecoEntradaFornecedor } from '../../AlteracaoPrecoEntradaFornecedorContext';
import { Container } from '../../styles';
import { NFItens } from '../../types';

const TABLE_HEIGHT = 400;
const DESCRIPTION_MAX_WIDTH = '18.75rem';
const EMPTY_STATE_COLSPAN = 10;

const getVendaPraticarFieldName = (codProduto: string): string =>
  `venda_praticar_${codProduto}`;

const getVariationColor = (variation: number): string => {
  if (variation > 0) return 'red';
  if (variation < 0) return 'blue';

  return '#212121';
};

const getRowKey = (item: NFItens, index: number): string =>
  `${item.num_item}-${item.cod_produto}-${index}`;

const TableContainerComponent: React.FC = () => {
  const {
    register,
    control,
    errors,
    loadingSug,
    itensNF,
    handleRowClick,
    selectedRowIndex,
    onKeyUpInputMoney,
    user,
    tableContainerRef,
    handleGridKeyDown,
  } = useAlteracaoPrecoEntradaFornecedor();

  const firstInputCellRef = useRef<HTMLTableCellElement | null>(null);
  const hasAutoFocusedRef = useRef(false);

  const firstItemCode = itensNF[0]?.cod_produto ?? '';

  useEffect(() => {
    if (loadingSug) {
      hasAutoFocusedRef.current = false;
      return;
    }

    if (!itensNF.length || hasAutoFocusedRef.current) return;

    const timeoutId = window.setTimeout(() => {
      const inputName = getVendaPraticarFieldName(firstItemCode);

      const firstInput =
        firstInputCellRef.current?.querySelector<HTMLInputElement>(
          `[name="${inputName}"]`,
        ) ?? null;

      if (firstInput) {
        firstInput.focus();
        hasAutoFocusedRef.current = true;
      }
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loadingSug, itensNF.length, firstItemCode]);

  useEffect(() => {
    if (itensNF.length === 0) {
      hasAutoFocusedRef.current = false;
    }
  }, [itensNF.length]);

  const handleCopyToClipboard = useCallback(async (text: string) => {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API indisponível');

      await navigator.clipboard.writeText(text);
      toast.success('Código copiado para área de transferência');
    } catch {
      toast.warning('Não foi possível copiar');
    }
  }, []);

  const getTooltipTitle = useCallback((description: string) => {
    if (description.length <= 17) return '';

    return <span style={{ fontSize: '0.8125rem' }}>{description}</span>;
  }, []);

  const fieldErrors = useMemo(
    () => (errors as Record<string, unknown> | undefined) ?? {},
    [errors],
  );

  if (loadingSug) {
    return (
      <Paper>
        <Container>
          <DefaultLoader />
        </Container>
      </Paper>
    );
  }

  return (
    <Paper>
      <div
        className="col-sm-12 col-md-12 mt-4"
        ref={tableContainerRef}
        onKeyDownCapture={handleGridKeyDown}
      >
        <TableContainer
          style={{
            maxHeight: TABLE_HEIGHT,
            minHeight: TABLE_HEIGHT,
            position: 'relative',
          }}
        >
          <Table stickyHeader aria-label="Tabela de itens da nota fiscal">
            <TableHead>
              <TableRow>
                <TableCell className="cellModify">Item</TableCell>

                <TableCell
                  className="cellModify"
                  align="center"
                  style={{ minWidth: '100px' }}
                >
                  Código
                </TableCell>

                <TableCell
                  className="cellModify"
                  style={{ minWidth: DESCRIPTION_MAX_WIDTH }}
                >
                  Descrição
                </TableCell>

                <TableCell align="center" className="cellModify">
                  Alt?
                </TableCell>

                <TableCell align="center" className="cellModify">
                  Variação
                </TableCell>

                <TableCell align="center" className="cellModify">
                  Venda
                </TableCell>

                <TableCell
                  className="cellModify"
                  align="center"
                  style={{ minWidth: '150px' }}
                >
                  Venda Praticar
                </TableCell>

                <TableCell
                  className="cellModify"
                  align="center"
                  style={{ minWidth: '120px' }}
                >
                  Sugestão
                </TableCell>

                <TableCell align="center" className="cellModify">
                  Bonif?
                </TableCell>

                <TableCell
                  className="cellModify"
                  align="center"
                  style={{ minWidth: '150px' }}
                >
                  Histórico
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody id="tableBottom" style={{ height: '1.25rem' }}>
              {itensNF.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={EMPTY_STATE_COLSPAN} align="center">
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              ) : (
                itensNF.map((item: NFItens, index: number) => {
                  const fieldName = getVendaPraticarFieldName(item.cod_produto);
                  const hasFieldError = Boolean(fieldErrors[fieldName]);

                  return (
                    <TableRow
                      key={getRowKey(item, index)}
                      hover
                      onClick={() => handleRowClick(index, item, true)}
                      style={{
                        backgroundColor:
                          selectedRowIndex === index ? 'lightblue' : 'white',
                      }}
                    >
                      <TableCell>{item.num_item}</TableCell>

                      <TableCell
                        align="center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleCopyToClipboard(item.cod_produto)}
                      >
                        {item.cod_produto}
                      </TableCell>

                      <Tooltip
                        title={getTooltipTitle(item.des_produto)}
                        disableHoverListener={item.des_produto.length <= 17}
                        arrow
                        PopperProps={{ style: { marginTop: -35 } }}
                      >
                        <TableCell
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: DESCRIPTION_MAX_WIDTH,
                          }}
                        >
                          {item.des_produto}
                        </TableCell>
                      </Tooltip>

                      <TableCell
                        align="center"
                        style={{
                          color: item.flg_custo_alterado ? 'red' : '#212121',
                        }}
                      >
                        {item.flg_custo_alterado ? 'Sim' : 'Não'}
                      </TableCell>

                      <TableCell
                        align="center"
                        style={{
                          color: getVariationColor(
                            Number(item.per_variacao_custo),
                          ),
                        }}
                      >
                        {formatCurrencyAsText(Number(item.per_variacao_custo))}%
                      </TableCell>

                      <TableCell
                        align="center"
                        style={{
                          color: item.val_venda === 0 ? 'red' : 'black',
                        }}
                      >
                        {formatCurrencyAsText(item.val_venda)}
                      </TableCell>

                      <TableCell
                        ref={index === 0 ? firstInputCellRef : undefined}
                      >
                        <InputMoney
                          className="form-control moneyTable"
                          label=""
                          placeholder="0,00"
                          maxLength={10}
                          min={0.01}
                          max={999999.01}
                          name={fieldName}
                          register={register}
                          isError={hasFieldError}
                          showIcon={false}
                          control={control}
                          onKeyUp={(event) => {
                            onKeyUpInputMoney(item, event.target, fieldName);
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        {formatCurrencyAsText(Number(item.calcSugVda))}
                      </TableCell>

                      <TableCell align="center">
                        {item.flg_bonificado ? 'Sim' : 'Não'}
                      </TableCell>

                      <TableCell className="svg">
                        <HistoricoProduto
                          codProduto={item.cod_produto}
                          codLoja={item.cod_loja}
                          user={user}
                          hasIcone
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Paper>
  );
};

export default TableContainerComponent;
